import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CustomerService } from '../../services/customerService';
import { Plus, Clock, CheckCircle, AlertCircle, Wrench, Car, Calendar } from 'lucide-react';

const CustomerServices = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [serviceType, setServiceType] = useState('');
  const [description, setDescription] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [urgency, setUrgency] = useState('medium');

  const { data: serviceRequests = [], isLoading, refetch } = useQuery({
    queryKey: ['customerServiceRequests', user?.id],
    queryFn: () => CustomerService.getServiceRequests(user?.id),
    enabled: !!user?.id,
  });

  const handleCreateRequest = async () => {
    if (!serviceType || !description) {
      alert('Please fill in all required fields');
      return;
    }
    const result = await CustomerService.createServiceRequest({
      customer_id: user?.id,
      service_type: serviceType,
      description,
      preferred_date: preferredDate,
      urgency,
    });
    if (result.success) {
      alert('Service request created successfully!');
      setShowCreateModal(false);
      setServiceType('');
      setDescription('');
      setPreferredDate('');
      setUrgency('medium');
      refetch();
    } else {
      alert('Failed to create service request: ' + result.error);
    }
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'in_progress') return 'bg-blue-100 text-blue-700';
    if (s === 'pending') return 'bg-yellow-100 text-yellow-700';
    if (s === 'cancelled') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getUrgencyColor = (urgency) => {
    const u = urgency?.toLowerCase() || '';
    if (u === 'urgent') return 'text-red-600';
    if (u === 'high') return 'text-orange-600';
    if (u === 'medium') return 'text-yellow-600';
    return 'text-green-600';
  };

  const serviceTypes = [
    { id: 'roadside', name: 'Roadside Assistance', icon: '🚗' },
    { id: 'periodic', name: 'Periodic Service', icon: '📅' },
    { id: 'ac', name: 'AC Service', icon: '❄️' },
    { id: 'battery', name: 'Battery Replacement', icon: '🔋' },
    { id: 'tyre', name: 'Tyre Change', icon: '🛞' },
    { id: 'repair', name: 'Repair', icon: '🔧' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Services</h1>
          <p className="text-gray-600 mt-1">Book and track your service requests</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
        >
          <Plus size={20} />
          <span>New Service Request</span>
        </button>
      </div>

      {/* Service Types Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {serviceTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => {
              if (type.id === 'periodic') {
                navigate('/customer/services/periodic');
              } else {
                setServiceType(type.id);
                setShowCreateModal(true);
              }
            }}
            className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition text-center"
          >
            <div className="text-3xl mb-2">{type.icon}</div>
            <p className="text-sm font-medium text-gray-900">{type.name}</p>
          </button>
        ))}
      </div>

      {/* Service Requests List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Service Requests</h2>
        </div>
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Loading services...</p>
          </div>
        ) : serviceRequests.length === 0 ? (
          <div className="p-12 text-center">
            <Wrench size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 mb-4">No service requests yet</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              Create Your First Request
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {serviceRequests.map((request) => (
              <div key={request.id} className="p-6 hover:bg-gray-50 transition">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{request.service_type || 'Service Request'}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(request.status)}`}>
                        {request.status || 'Pending'}
                      </span>
                      {request.urgency && (
                        <span className={`text-sm font-medium ${getUrgencyColor(request.urgency)}`}>
                          {request.urgency.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-2">{request.description || request.problem || 'No description'}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {request.preferred_date && (
                        <div className="flex items-center space-x-1">
                          <Calendar size={16} />
                          <span>{new Date(request.preferred_date).toLocaleDateString()}</span>
                        </div>
                      )}
                      {request.created_at && (
                        <div className="flex items-center space-x-1">
                          <Clock size={16} />
                          <span>Created {new Date(request.created_at).toLocaleDateString()}</span>
                        </div>
                      )}
                      {request.estimated_cost && (
                        <div className="flex items-center space-x-1">
                          <span>₹</span>
                          <span>{Number(request.estimated_cost).toLocaleString('en-IN')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Request Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">New Service Request</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Type *</label>
                <select
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select service type</option>
                  {serviceTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Describe your service requirement..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date</label>
                <input
                  type="date"
                  value={preferredDate}
                  onChange={(e) => setPreferredDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Urgency</label>
                <select
                  value={urgency}
                  onChange={(e) => setUrgency(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRequest}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                  Create Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerServices;
